<template>
  <table class="table-hover reports-table timesheets-report-table table">
    <TimesheetsReportTableHeader />

    <template v-for="section in tableData.sections">
      <!-- eslint-disable vue/require-v-for-key -->
      <tbody class="grouping-header">
        <tr>
          <th :colspan="tableColCount">
            <template v-if="walker.main_type === 'timesheet'">
              <ReportTableAvatar
                :user-id="section.rows[0].record.user_id"
                :url="section.rows[0].record.avatar_url"
              />
              <template v-if="timesheetLinkColumn === 'timesheet'">
                <a
                  v-tooltip
                  target="_blank"
                  :href="timesheetLink(section.rows[0].record.timesheet_id)"
                  :title="timesheetLinkTitle(section.rows[0].record.start_date, section.rows[0].record.stop_date)"
                >
                  {{ make_possessive(section.rows[0].record.user_name) }}
                  for period
                  {{ section.rows[0].record.start_date | formatDate }}
                  to
                  {{ section.rows[0].record.stop_date | formatDate }}
                </a>
              </template>
              <template v-else>
                {{ make_possessive(section.rows[0].record.user_name) }}
                for period
                {{ section.rows[0].record.start_date | formatDate }}
                to
                {{ section.rows[0].record.stop_date | formatDate }}
              </template>
            </template>
            <template v-else-if="walker.main_type === 'member'">
              <ReportTableAvatar
                :user-id="section.main_id"
                :url="section.rows[0].record.avatar_url"
              />
              {{ section.main }}
            </template>
            <template v-else-if="walker.main_type === 'date'">
              {{ format_date_label(section.main) }}
            </template>
            <template v-else>
              {{ section.main }}
            </template>
          </th>
        </tr>
      </tbody>

      <tbody
        class="ttotal"
        :class="{ 'is-expanded': showToggledSectionById(section.main_id) }"
        @click="toggleSection(section)"
      >
        <tr class="cursor-pointer">
          <td class="totals-row">
            <span
              class="expand-rows-toggle"
              :class="{ 'is-expanded': showToggledSectionById(section.main_id) }"
            >
              <i class="hi hi-caret" />
              <span class="">Total</span>
            </span>
          </td>
          <td v-if="walker.sub_show && walker.third_show" />
          <td v-if="tableShowMemberInfo" />
          <td v-if="tableShowMemberEmployment" />
          <td>
            {{ section.total.status | mixedStatus }}
          </td>
          <td>{{ section.total.tracked | formatDuration('auto', null, false, true) }}</td>
          <td v-if="tableShowActivity">
            {{ section.total.overall | percentage(section.total.input_tracked) }}
          </td>
          <td v-if="tableShowApprovedOn" />
          <td v-if="tableShowApprovedBy" />
          <td v-if="previousReportQueryFilters.show_pay_rate">
            <template v-if="(viewPaymentInfo || !tableIsTeam) && hasArrayPositiveValues(section.total.pay_rate)">
              {{ mixedCurrency(section.total.pay_rate, organization.currency) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="tableShowSpent">
            <template v-if="(viewPaymentInfo || !tableIsTeam) && isPositiveValue(section.total.spent_amount)">
              {{ section.total.spent_amount | currency(organization.currency) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="previousReportQueryFilters.show_bill_rate">
            <template v-if="viewPaymentInfo && hasArrayPositiveValues(section.total.bill_rate)">
              {{ mixedCurrency(section.total.bill_rate, organization.currency) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="previousReportQueryFilters.show_billed">
            <template v-if="viewPaymentInfo && isPositiveValue(section.total.billed_amount)">
              {{ section.total.billed_amount | currency(organization.currency) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="tableShowBillableTime">
            <template v-if="viewPaymentInfo && section.total.billable_time">
              {{ section.total.billable_time | formatDuration('auto', null, false, true) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="tableShowNonBillableTime">
            <template v-if="viewPaymentInfo && section.total.billable_time">
              {{ (section.total.tracked - section.total.billable_time) | formatDuration('auto', null, false, true) }}
            </template>
            <template v-else>—</template>
          </td>
        </tr>
      </tbody>

      <tbody
        v-show="showToggledSectionById(section.main_id)"
        class="tbody"
      >
        <tr
          v-for="(row, index) in section.rows"
          :class="row_class(row.sub_show_data)"
        >
          <template v-if="memberInfoColumnPosition === 0">
            <td v-if="tableShowMemberInfo">
              <template v-if="index === 0">
                <ReportMemberFields
                  type="info"
                  :data="row.record"
                  :fields="infoFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowMemberEmployment">
              <template v-if="index === 0">
                <ReportMemberFields
                  type="employment"
                  :data="row.record"
                  :fields="employmentFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
          </template>
          <td v-if="walker.sub_show">
            <template v-if="row.sub_show_data">
              <template v-if="walker.sub_type === 'member'">
                <ReportTableAvatar
                  :user-id="row.record.sub_id"
                  :url="row.record.avatar_url"
                />
                <template v-if="timesheetLinkColumn === 'member'">
                  <a
                    v-tooltip
                    target="_blank"
                    :href="timesheetLink(row.record.timesheet_id)"
                    :title="timesheetLinkTitle(row.record.start_date, row.record.stop_date)"
                  >
                    {{ row.record.sub }}
                  </a>
                </template>
                <template v-else>
                  {{ row.record.sub }}
                </template>
              </template>
              <template v-else-if="walker.sub_type === 'date'">
                <template v-if="timesheetLinkColumn === 'date'">
                  <a
                    v-tooltip
                    target="_blank"
                    :href="timesheetLink(row.record.timesheet_id)"
                    :title="timesheetLinkTitle(row.record.start_date, row.record.stop_date)"
                  >
                    {{ format_date_label(row.record.sub) }}
                  </a>
                </template>
                <template v-else>
                  {{ format_date_label(row.record.sub) }}
                </template>
              </template>
              <template v-else>
                {{ row.record.sub }}
              </template>
            </template>
          </td>
          <template v-if="memberInfoColumnPosition === 1">
            <td v-if="tableShowMemberInfo">
              <template v-if="row.sub_show_data">
                <ReportMemberFields
                  type="info"
                  :data="row.record"
                  :fields="infoFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowMemberEmployment">
              <template v-if="row.sub_show_data">
                <ReportMemberFields
                  type="employment"
                  :data="row.record"
                  :fields="employmentFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
          </template>
          <td v-if="walker.third_show">
            <template v-if="walker.third_type === 'project'">
              <template v-if="row.record.third">
                <ReportTableAvatarInitial
                  :text="row.record.third"
                  class-modifier="avatar avatar-speck"
                />
                <template v-if="timesheetLinkColumn === 'project'">
                  <a
                    v-tooltip
                    target="_blank"
                    :href="timesheetLink(row.record.timesheet_id)"
                    :title="timesheetLinkTitle(row.record.start_date, row.record.stop_date)"
                  >
                    {{ row.record.third }}
                  </a>
                </template>
                <template v-else>
                  {{ row.record.third }}
                </template>
              </template>
              <template v-else>
                —
              </template>
            </template>
            <template v-else>
              {{ row.record.third }}
            </template>
          </td>
          <td>
            {{ row.record.status | formatStatus }}
          </td>
          <td class="duration">
            {{ row.record.tracked | formatDuration('auto', null, false, true) }}
          </td>
          <td v-if="tableShowActivity">
            <template v-if="row.record.activity">
              {{ row.record.activity | percentage(100) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="tableShowApprovedOn">
            <template v-if="row.record.approved_on">
              {{ row.record.approved_on | formatDate }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="tableShowApprovedBy">
            <template v-if="row.record.approved_by_id && row.record.approved_by_name">
              <ReportTableAvatar
                :user-id="row.record.approved_by_id"
                :url="row.record.approved_by_avatar_url"
              />
              {{ row.record.approved_by_name }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="previousReportQueryFilters.show_pay_rate">
            <template v-if="(viewPaymentInfo || !tableIsTeam) && isPositiveValue(row.record.pay_rate)">
              {{ row.record.pay_rate | currency(organization.currency) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="tableShowSpent">
            <template v-if="(viewPaymentInfo || !tableIsTeam) && isPositiveValue(row.record.spent_amount)">
              {{ row.record.spent_amount | currency(organization.currency) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="previousReportQueryFilters.show_bill_rate">
            <template v-if="viewPaymentInfo && isPositiveValue(row.record.bill_rate)">
              {{ row.record.bill_rate | currency(organization.currency) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="previousReportQueryFilters.show_billed">
            <template v-if="viewPaymentInfo && isPositiveValue(row.record.billed_amount)">
              {{ row.record.billed_amount | currency(organization.currency) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="tableShowBillableTime">
            <template v-if="viewPaymentInfo && row.record.billable_time">
              {{ row.record.billable_time | formatDuration('auto', null, false, true) }}
            </template>
            <template v-else>—</template>
          </td>
          <td v-if="tableShowNonBillableTime">
            <template v-if="viewPaymentInfo && row.record.billable_time">
              {{ (row.record.tracked - row.record.billable_time) | formatDuration('auto', null, false, true) }}
            </template>
            <template v-else>—</template>
          </td>
        </tr>
      </tbody>
    </template>
    <!-- eslint-enable vue/require-v-for-key -->
  </table>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { compact, isEmpty } from 'lodash-es';
import { TIMESHEET_STATUSES } from '~/app_constants';
import { TimesheetsResource } from '~/resources';
import TimesheetsReportTableHeader from './TimesheetsReportTableHeader.vue';

export default {
  name: 'TimesheetsReportTable',
  components: { TimesheetsReportTableHeader },
  filters: {
    mixedStatus(status) {
      if (status.length > 1) {
        return 'Mixed';
      }

      return TIMESHEET_STATUSES[status[0]];
    },
    formatStatus(status) {
      return TIMESHEET_STATUSES[status];
    },
  },
  props: {
    tableData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters([
      'employmentFields',
      'infoFields',
      'isOwner',
      'memberInfoColumnPosition',
      'previousReportQueryFilters',
      'showToggledSectionById',
      'walker',
      'tableColCount',
      'tableIsTeam',
      'tableShowActivity',
      'tableShowApprovedBy',
      'tableShowApprovedOn',
      'tableShowBillableTime',
      'tableShowMemberEmployment',
      'tableShowMemberInfo',
      'tableShowNonBillableTime',
      'tableShowSpent',
      'tableSumDateRanges',
      'viewPaymentInfo',
    ]),
    ...mapState({
      organization: 'organization',
    }),
    timesheetLinkColumn() {
      if (this.walker.main_type === 'timesheet') {
        return 'timesheet';
      }

      if (this.tableSumDateRanges) {
        return 'project';
      }

      return this.walker.sub_type;
    },
  },
  methods: {
    row_class(new_record) {
      return new_record ? 'has-border' : '';
    },
    make_possessive(value) {
      return `${value}'s`;
    },
    format_date_label(value) {
      if (this.isSummedDate(value)) {
        return (
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date)) +
          ' - ' +
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date_end))
        );
      }

      return TimeUtils.format_date(TimeUtils.parse_internal_date(value));
    },
    toggleSection(section) {
      this.$store.dispatch('toggleSection', {
        section_id: section.main_id,
        section_count: this.tableData.sections.length,
      });
    },
    mixedCurrency(value, currency) {
      if (value.length > 1) {
        return 'Mixed';
      }

      return this.$options.filters.currency(value[0], currency);
    },
    timesheetLink(id) {
      return TimesheetsResource.timesheetLink({ timesheetId: id });
    },
    timesheetLinkTitle(startDate, stopDate) {
      return `Open ${startDate} - ${stopDate} timesheet`;
    },
    hasArrayPositiveValues(array) {
      const compacted = compact(array);

      if (isEmpty(compacted)) {
        return false;
      }

      return compacted.findIndex((item) => this.isPositiveValue(item)) > -1;
    },
    isPositiveValue(value) {
      return value > 0;
    },
    isSummedDate(date) {
      return this.tableSumDateRanges && date === 'summed_date';
    },
  },
};
</script>
